import React, { useState, useEffect, useRef } from "react";
import MilestonesModal from "@pages/Program/components/milestones/MilestonesModal";
import { useGoal } from "@pages/Program/components/goal/utils/useGoal";
import { GoalItem } from "@shared/interfaces/ProgramFiles";
import EditGoal from "@pages/Program/components/goal/EditGoal";

import { HiDotsVertical } from "react-icons/hi";
import { GoalStatus } from "@pages/Program/utils/enums";
import { toastService } from "@services/ToastService";
import DeleteEntityModal from "@pages/Program/components/DeleteEntity";
import GoalModal from "./components/GoalModal";
import Pill from "@shared/components/Pill";

const statusColorMap: Record<GoalItem["status"], string> = {
  on_track: "text-[#009947] border-[#009947] bg-[#EAFBF2] border",
  behind: "text-[#F29F04] border-[#F29F04] bg-[#FFF6E6] border",
  at_risk: "text-[#F04438] border-[#F04438] bg-[#FEF3F2] border",
};

interface GoalTableProps {
  goals: GoalItem[];
  fetchGoals: () => void;
  startDate?: string;
  endDate?: string;
}

const GoalGrid: React.FC<GoalTableProps> = ({
  goals,
  fetchGoals,
  startDate,
  endDate,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGoalModalOpen, setIsGoalModalOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<GoalItem | null>(null);
  const [selectedGoalId, setSelectedGoalId] = useState<string>("");
  const [isEditGoalOpen, setIsEditGoalOpen] = useState(false);
  const [openPopupId, setOpenPopupId] = useState<string | null>(null);
  const popupRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const { deleteGoal } = useGoal();

  const handleOpenModal = (goal: GoalItem) => {
    setSelectedGoal(goal);
    setIsModalOpen(true);
  };

  const handleOpenGoalModal = (goal: GoalItem) => {
    setSelectedGoal(goal);
    setIsGoalModalOpen(true);
  };

  const handleEditGoal = (goalId: string) => {
    setSelectedGoalId(goalId);
    setIsEditGoalOpen(true);
    setOpenPopupId(null);
  };

  const togglePopup = (goalId: string) => {
    setOpenPopupId((prev) => (prev === goalId ? null : goalId));
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<GoalItem>(
    {} as GoalItem
  );

  const handleDeleteClick = (entity: GoalItem) => {
    setSelectedEntity(entity);
    setModalOpen(true);
  };

  const handleDeleteGoal = async () => {
    try {
      await deleteGoal(selectedEntity.id);
      fetchGoals();
    } catch (error) {
      console.error("Failed to delete goal:", error);
      toastService.error("Failed to delete goal, Try Again Later");
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openPopupId) {
        const ref = popupRefs.current[openPopupId];
        if (ref && !ref.contains(event.target as Node)) {
          setOpenPopupId(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openPopupId]);

  return (
    <>
      <div className="border rounded-lg flex flex-col bg-white w-full max-h-[380px] min-h-[380px] overflow-hidden text-sm 2xl:text-base">
        {/* Header */}
        <div className="grid grid-cols-6 gap-4 px-4 py-4 font-semibold text-gray-700 border-b bg-white rounded-t-lg text-center">
          <div className="text-left">Goals</div>
          <div className="text-left">Open Tasks</div>
          <div>Start Date</div>
          <div>End Date</div>
          <div>Projects</div>
          <div className="text-center">Status</div>
        </div>

        <div className="overflow-y-auto flex-1 text-center">
          {/* Rows */}
          {goals.map((item, idx) => (
            <div
              key={idx}
              className="grid grid-cols-6 gap-4 px-4 py-4 border-b text-gray-800 "
            >
              <div
                className="text-left flex items-center text-blue-600 cursor-pointer"
                onClick={() => handleOpenGoalModal(item)}
              >
                {item.name}
              </div>
              <div
                className="text-blue-600 cursor-pointer whitespace-nowrap text-left flex items-center"
                onClick={() => handleOpenModal(item)}
              >
                {item.milestones.length} Open Tasks
              </div>
              <div className="whitespace-nowrap flex items-center justify-center">
                {item.start_date}
              </div>
              <div className="whitespace-nowrap flex items-center justify-center">
                {item.end_date}
              </div>
              <div className="whitespace-pre-line flex flex-wrap space-x-0 justify-center">
                {item.projects.map((project, index) => (
                  <div
                    className="flex items-center self-center p-1"
                    key={index}
                  >
                    <Pill
                      styling={`bg-blue-100 text-blue-700 border-blue-700 px-[0.5rem!important]`}
                      text={project.name}
                    />
                  </div>
                ))}
              </div>
              <div className="font-medium text-center justify-between items-center flex relative">
                <div></div>
                <Pill
                  styling={`${statusColorMap[item.status]} mr-[-1.75rem]`}
                  text={GoalStatus[item.status]}
                />
                {/* <p
                  className={`text-[0.75rem] leading-6 font-medium px-5 rounded-lg whitespace-nowrap mr-[-1.75rem] ${
                    statusColorMap[item.status]
                  }`}
                >
                  {GoalStatus[item.status]}
                </p> */}
                <div className="relative ml-2 w-6">
                  <button
                    onClick={() => togglePopup(item.id)}
                    className="text-gray-500 hover:text-gray-700 p-1 rounded-full focus:outline-none"
                  >
                    <HiDotsVertical size={18} />
                  </button>

                  {openPopupId === item.id && (
                    <div
                      ref={(ref) => {
                        popupRefs.current[item.id] = ref;
                      }}
                      className="absolute right-0 mt-2 w-28 bg-white border border-gray-200 rounded-md shadow-lg z-20"
                    >
                      <button
                        onClick={() => handleEditGoal(item.id)}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteClick(item)}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {startDate && endDate && (
        <EditGoal
          isOpen={isEditGoalOpen}
          onClose={() => setIsEditGoalOpen(false)}
          goalId={selectedGoalId}
          fetchGoals={fetchGoals}
          startDate={startDate}
          endDate={endDate}
        />
      )}
      <DeleteEntityModal
        isOpen={modalOpen}
        selectedEntity={selectedEntity}
        onClose={() => setModalOpen(false)}
        onConfirm={handleDeleteGoal}
      />
      {/* Modal */}
      {isModalOpen && selectedGoal && (
        <MilestonesModal
          title={selectedGoal.name}
          milestones={selectedGoal.milestones}
          onClose={() => setIsModalOpen(false)}
        />
      )}

      {isGoalModalOpen && selectedGoal && (
        <GoalModal
          title={"Goal Details"}
          goal={selectedGoal}
          onClose={() => setIsGoalModalOpen(false)}
        />
      )}
    </>
  );
};

export default GoalGrid;
